// The wilds as a big island of gathering grounds (plazmi.wilds-land,
// resources/wilds.edn): the woodlot, the quarry, the clay pit by the stream,
// the flax fields, the sheep meadow, the seed meadow and orchard, the pond
// and the scrapyard, along winding paths from a plank bridge, with benches,
// a picnic spot and a lookout, and a signpost naming each ground. Each ground
// belongs to one of the four places the server gathers at (the woodlot, the
// quarry, the meadow, the scrapyard), whose rules don't change; commons.ts
// walks a plazmi out to the ground for what it's gathering.
//
// Hundreds of trees, flax plants, flowers, reeds and stones stay cheap: one
// instanced mesh per kind, and the rest merged by material.
import * as THREE from 'three';
import { mat, cyl, sphere, rbox, at, lathe, finishShadows } from './kit.js';
import { wbox } from './house.js';
import { surf } from './surfaces.js';
import * as Y from './yard.js';
import { rng } from './util.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  signboard, fingerArm, mushrooms, fallenLog, ladder, shovelAndBucket, crates, bicycle, logPile, leanTo, sawhorse, stumpWithHatchet,
  stoneBlocks, winch, scrapHeap, shed, cartWheel, fence, heap, sheep,
} from './wilds.js';
import { type WildsLayout, type Ground, type XZ, M, inst, blobGeo, tintMat, forest, ribbon, strip, patch, outlineIsland, lawnMaterial, inside, walkStrips, type TreeSpot } from './lands.js';

const FLOWERS = ['#e86a92', '#f2c14e', '#ffffff', '#9b7fc9', '#f6a6b8', '#ffffff', '#f08f5a'];

// where a plazmi gathering a material stands to work at a ground, by the thing it works at (the clearing's
// chopping spot, the quarry face, the clay pit, the sandy bank, the flax rows, the sheep, between the orchard's
// rows, between its trees, the scrap heaps; clear of the stream through the clay pit), and the heap it carries what it got to, if it carries it anywhere (world tiles)
export function workSpots(g: Ground, material: string): { spots: XZ[]; heap?: XZ } {
  const [x, z] = g.at, o = (dx: number, dz: number): XZ => [x + dx, z + dz];
  switch (g.kind) {
    case 'wood': return ['sticks', 'leaves'].includes(material)
      ? { spots: [o(-6.5, 6.8), o(3.2, 6.6), o(-9.5, 1.5)], heap: o(-2.9, 3.9) }
      : { spots: [o(-1.4, -1.4), o(2.4, -0.6), o(-1.2, 1.6), o(5.6, -1.6)], heap: o(-3.9, -1.0) };
    case 'rock': return { spots: [o(-2, -2.6), o(2, -2.4), o(-9, -1.8), o(7.5, -2)], heap: o(0.8, 8.6) };
    case 'clay': return material === 'sand'
      ? { spots: [o(8, -5), o(10, -3.6)], heap: o(7, -1) }
      : { spots: [o(-3.5, 0.5), o(-5, 2.6), o(-4.5, -2)], heap: o(-5.6, 4.2) };
    case 'flax': return { spots: [o(-8, -5), o(9, -6), o(-14, 6), o(7, 6)] };
    case 'pasture': return { spots: [o(-3, 5), o(4, 3), o(-9, -3)] };
    case 'orchard': return { spots: [o(-1.05, 0.6), o(5.45, -5.4), o(-7.55, 6.6)] };
    case 'scrap': return { spots: [o(-6, 0.5), o(-3.6, 4.2), o(5.8, 3), o(1, -3)] };
    default: return { spots: g.work.slice(0, 4) };
  }
}
const WORK_MATERIALS = ['timber', 'sticks', 'stone', 'clay', 'sand', 'flax', 'wool', 'flowers', 'iron'];

export class WildsLand {
  root = new THREE.Group();
  // the small things (flowers, flax, reeds, leaves, pebbles): drawn only when the camera is near enough to see them
  detail = new THREE.Group();
  L: WildsLayout;
  grounds: Ground[];
  hits: THREE.Object3D[] = [];
  pools: [number, number, number][] = [];
  private byId = new Map<string, Ground>();
  private trees: TreeSpot[] = [];
  private keepOut: { at: XZ; r: number }[] = [];
  private noTrees: Ground[] = [];

  constructor(L: WildsLayout) {
    this.L = L; this.grounds = L.grounds;
    for (const g of L.grounds) this.byId.set(g.id, g);
    // where plazmi gather stays clear of trees, boulders and mushrooms
    for (const g of L.grounds) for (const m of WORK_MATERIALS) { const w = workSpots(g, m); for (const p of w.spots) this.keepOut.push({ at: p, r: 1.6 }); if (w.heap) this.keepOut.push({ at: w.heap, r: 1.2 }); }
    const still = new THREE.Group();
    still.add(outlineIsland(L.outline, lawnMaterial(), { depth: 2.6, seed: 11, H: 70 }));
    this.bridge(still);
    this.paths(still);
    for (const g of L.grounds) {
      const f = (this as any)[`g_${g.kind}`] as ((g: Ground, root: THREE.Group) => void) | undefined;
      f?.call(this, g, still);
      this.signpost(g, still);
      const hit = new THREE.Mesh(new THREE.BoxGeometry(g.r[0] * 1.6, 0.2, g.r[1] * 1.6), new THREE.MeshBasicMaterial({ visible: false }));
      hit.position.set(g.at[0], 0.1, g.at[1]); hit.userData.wild = g.place; hit.userData.ground = g; hit.userData.keep = true;
      this.root.add(hit); this.hits.push(hit);
    }
    this.rests(still);
    this.meadowAll(still);
    finishShadows(still);
    this.root.add(still);
    this.root.add(forest(this.trees, 91));
    this.root.add(this.detail);
  }

  // each frame: the small things only within sight
  frame(cam: THREE.Camera) {
    const v = this.view().target, d = Math.hypot(cam.position.x - v.x, cam.position.z - v.z) + cam.position.y * 0.6;
    this.detail.visible = d < 520;
  }

  ground(id: string) { return this.byId.get(id) || null; }
  // the ground a plazmi gathering this (the plural word: "clay", "sticks") works at, in a place
  groundFor(place: string, material?: string) {
    const gs = this.grounds.filter((g) => g.place === place);
    return gs.find((g) => material && g.gives.includes(material)) || gs[0];
  }
  walkGrounds() {
    const out = walkStrips(this.L.outline, 2);
    const { from, to, w } = this.L.bridge;
    out.push({ x0: from[0] - 0.4, z0: from[1] - w / 2, x1: to[0] + 0.6, z1: from[1] + w / 2, y: -0.1 });
    return out;
  }
  arrival() { return { x: this.L.bridge.to[0] + 2.5, z: this.L.bridge.to[1], yaw: Math.PI / 2 }; }
  view() {
    const xs = this.L.outline.map((p) => p[0]), zs = this.L.outline.map((p) => p[1]);
    return { target: new THREE.Vector3((Math.min(...xs) + Math.max(...xs)) / 2, 0, (Math.min(...zs) + Math.max(...zs)) / 2 + 8), dist: 560 };
  }
  private clear(x: number, z: number, pad = 0) {
    return inside(this.L.outline, x, z) && !this.keepOut.some((k) => Math.hypot(x - k.at[0], z - k.at[1]) < k.r + pad);
  }
  private inGround(g: Ground, x: number, z: number, k = 1) { return ((x - g.at[0]) / (g.r[0] * k)) ** 2 + ((z - g.at[1]) / (g.r[1] * k)) ** 2 < 1; }

  // ----- the plank bridge from the plaza's east path

  private bridge(root: THREE.Group) {
    const { from, to, w } = this.L.bridge;
    root.add(strip([from[0] - 0.4, from[1]], [to[0] + 0.8, to[1]], w, -0.1, surf({ kind: 'planks', color: '#a87c4f', board: 0.2 }), 0.18));
    for (const s of [-1, 1]) for (let x = from[0] + 0.9; x < to[0]; x += 1.6) root.add(at(cyl(0.05, 0.05, 0.9, mat('#6e4b33'), 6), x, 0.25, from[1] + s * (w / 2 + 0.2)));
  }

  // ----- the winding paths and the stream

  private paths(root: THREE.Group) {
    const dirt = surf({ kind: 'dirt', color: '#cdb58c' });
    this.L.paths.forEach((p, i) => {
      root.add(ribbon(p.pts, p.w, 0.012 + i * 0.0004, dirt, { wobble: 0.25, seed: i + 5 }));
      const c = new THREE.CatmullRomCurve3(p.pts.map(([x, z]) => new THREE.Vector3(x, 0, z)), false, 'centripetal');
      for (const q of c.getSpacedPoints(Math.ceil(c.getLength() / 1.6))) this.keepOut.push({ at: [q.x, q.z], r: p.w / 2 + 0.8 });
    });
    // a fingerpost at the crossing, an arm toward each ground near it
    const [cx, cz] = this.L.crossing, post = new THREE.Group(), wood = mat('#6e4b33', { rough: 0.8 });
    post.add(at(cyl(0.08, 0.09, 3.6, wood, 8), 0, 1.8, 0)); post.add(at(sphere(0.12, wood, 8, 6), 0, 3.62, 0));
    const near = [...this.grounds].sort((a, b) => Math.hypot(a.at[0] - cx, a.at[1] - cz) - Math.hypot(b.at[0] - cx, b.at[1] - cz)).slice(0, 5);
    near.forEach((g, i) => {
      const ang = Math.atan2(-(g.at[1] - cz), g.at[0] - cx);
      const a = fingerArm(g.name); a.position.set(Math.cos(ang) * 1.0, 3.2 - i * 0.4, -Math.sin(ang) * 1.0); a.rotation.y = ang; post.add(a);
    });
    root.add(at(post, cx + 1.8, 0, cz - 1.8));
    this.keepOut.push({ at: [cx, cz], r: 4 });
    // the stream: a ribbon of water a touch below the grass, with pebbled banks, from its spring to the pond
    const s = this.L.stream;
    const water = mat('#7fb6cf', { rough: 0.12, metal: 0.1 });
    root.add(ribbon(s.pts, s.w, 0.03, water, { wobble: 0.3, seed: 2 }));
    const curve = new THREE.CatmullRomCurve3(s.pts.map(([x, z]) => new THREE.Vector3(x, 0, z)), false, 'centripetal');
    const pts = curve.getPoints(s.pts.length * 14), r = rng(5), peb: THREE.Matrix4[] = [], pc: THREE.Color[] = [];
    pts.forEach((p, i) => {
      this.keepOut.push({ at: [p.x, p.z], r: s.w });
      const t = pts[Math.min(pts.length - 1, i + 1)].clone().sub(pts[Math.max(0, i - 1)]).normalize();
      for (const sg of [-1, 1]) { const d = s.w / 2 + 0.15 + r() * 0.2; const sc = 0.08 + r() * 0.08; peb.push(M(p.x - t.z * d * sg, 0.03, p.z + t.x * d * sg, sc * 1.4, sc * 0.6, sc, r() * 6)); pc.push(new THREE.Color('#b9b2a4').offsetHSL(0, 0, (r() - 0.5) * 0.1)); }
    });
    this.detail.add(inst(blobGeo(0, false), tintMat(1), peb, pc));
    // stepping stones where the path from the quarry to the clay pit crosses it
    const sp = s.pts[Math.floor(s.pts.length * 0.35)];
    root.add(Y.steppingStones([[sp[0] - 1.6, sp[1]], [sp[0] - 0.6, sp[1] + 0.1], [sp[0] + 0.4, sp[1] - 0.05], [sp[0] + 1.4, sp[1] + 0.1]], 3));
    // the spring: a ring of stones where it rises
    const [qx, qz] = s.pts[0];
    for (let i = 0; i < 9; i++) { const a = (i / 9) * Math.PI * 2; const b = sphere(0.28 + r() * 0.12, mat('#a29f97', { rough: 1, flat: true }), 7, 5); b.scale.y = 0.6; b.position.set(qx + Math.cos(a) * 1.3, 0.08, qz + Math.sin(a) * 1.3); root.add(b); }
  }

  // ----- each ground's signpost, at its edge toward the crossing

  private signpost(g: Ground, root: THREE.Group) {
    const [cx, cz] = this.L.crossing, a = Math.atan2((cz - g.at[1]) / g.r[1], (cx - g.at[0]) / g.r[0]);
    const x = g.at[0] + Math.cos(a) * g.r[0] * 0.98, z = g.at[1] + Math.sin(a) * g.r[1] * 0.98;
    const sb = signboard(g.title, g.tag, { wood: '#5d7a3f', rock: '#6e6a62', clay: '#a8503a', flax: '#5f74b0', pasture: '#6f8f4a', orchard: '#8a6aa8', pond: '#3f7f9a', scrap: '#7a5a40' }[g.kind] || '#6e4b33');
    sb.position.set(x, 0, z); sb.rotation.y = Math.atan2(cx - x, cz - z);
    root.add(sb);
    const back = signboard(g.title, g.tag, '#6e4b33'); back.position.set(x, 0, z); back.rotation.y = sb.rotation.y + Math.PI; back.scale.set(0.98, 1, 0.98);
    root.add(back);
    this.keepOut.push({ at: [x, z], r: 2 });
  }

  // ----- the woodlot: beeches, birches and pines round a clearing with the log pile, the sawhorse and the stump

  g_wood(g: Ground, root: THREE.Group) {
    root.add(patch(g.at, g.r[0] * 1.05, g.r[1] * 1.05, 0.004, surf({ kind: 'grass', color: '#6f8a48' }), 21, 0.18));
    const [x, z] = g.at, r = rng(31);
    root.add(patch([x, z], 9, 7, 0.006, surf({ kind: 'dirt', color: '#b49a72' }), 4, 0.2));
    root.add(at(leanTo(), x + 3.5, 0, z - 4.5, 0.2));
    root.add(at(sawhorse(), x - 2.5, 0, z + 1.5, 0.5));
    root.add(at(stumpWithHatchet(), x + 0.5, 0, z + 3.5));
    root.add(at(logPile(9, 4), x - 5.5, 0, z - 2.5, 1.2));
    root.add(at(fallenLog(3.2), x + 6.5, 0, z + 2.5, 0.4));
    for (let i = 0; i < 5; i++) root.add(at(heap('stick', 5, 0.2, i), x - 4 + i * 2.2, 0, z + 5.2 - (i % 2) * 0.6));
    this.keepOut.push({ at: [x, z], r: 9 });
    for (const w of g.work) this.keepOut.push({ at: w, r: 2.2 });
    // leaf litter, russet and gold, under the trees
    const leaves: THREE.Matrix4[] = [], lc: THREE.Color[] = [];
    for (let i = 0; i < 420; i++) {
      const a = r() * Math.PI * 2, d = Math.sqrt(r()), px = x + Math.cos(a) * d * g.r[0], pz = z + Math.sin(a) * d * g.r[1];
      leaves.push(M(px, 0.01, pz, 0.35 + r() * 0.3, 0.02, 0.25 + r() * 0.2, r() * 6)); lc.push(new THREE.Color(['#b8743a', '#d4a24a', '#9a5a2e', '#c98a3a'][i % 4]));
    }
    this.detail.add(inst(blobGeo(0, false), tintMat(1), leaves, lc));
    for (let i = 0; i < 9; i++) { const px = x + (r() - 0.5) * g.r[0] * 1.4, pz = z + (r() - 0.5) * g.r[1] * 1.4; if (this.clear(px, pz) && this.inGround(g, px, pz)) root.add(at(mushrooms(i + 2), px, 0, pz)); }
    // the wood itself
    let n = 0;
    for (let i = 0; i < 1400 && n < 330; i++) {
      const a = r() * Math.PI * 2, d = Math.sqrt(r()) * 1.04, px = x + Math.cos(a) * d * g.r[0], pz = z + Math.sin(a) * d * g.r[1];
      if (!this.clear(px, pz, 0.6)) continue;
      if (this.trees.some(([tx, tz]) => Math.hypot(tx - px, tz - pz) < 2.6)) continue;
      const k = d > 0.75 ? ['birch', 'oak', 'beech'][n % 3] : ['beech', 'oak', 'pine', 'beech', 'birch', 'pine'][n % 6];
      this.trees.push([px, pz, k, 1.05 + r() * 0.35]); n++;
    }
  }

  // ----- the quarry: terraces of grey stone, boulders, cut blocks, a winch and a ladder

  g_rock(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r, r = rng(13);
    root.add(patch(g.at, rx * 1.02, rz * 1.02, 0.004, surf({ kind: 'dirt', color: '#bdb6a8' }), 9, 0.15));
    const st = surf({ kind: 'ashlar', color: '#b3afa6' }), st2 = surf({ kind: 'stone', color: '#9e9a92' });
    // the face: terraces stepping up to the back (north) of the quarry, each a run of blocks
    // of different lengths and heights, bending round like a bowl
    const st3 = surf({ kind: 'stone', color: '#aaa59b' });
    for (let i = 0; i < 4; i++) {
      const w = rx * (1.55 - i * 0.2), zc = z - rz * 0.2 - i * 3.2;
      let xx = x - w / 2;
      while (xx < x + w / 2 - 0.5) {
        const len = Math.min(x + w / 2 - xx, 2.2 + r() * 4.5), t = (xx + len / 2 - x) / (w / 2);
        const zb = zc + t * t * 3.5, h = 1.3 + i * 1.35 + (r() - 0.5) * 0.6;
        const b = wbox(xx, 0, zb - 3.2, xx + len - 0.08, h, zb, [st, st2, st3][Math.floor(r() * 3)]);
        b.rotation.y = -t * 0.12; root.add(b);
        xx += len;
      }
    }
    // a cart of cut stone on the floor, and chips of stone about
    const cart = new THREE.Group(), cw = mat('#8a6a4a', { rough: 0.85 });
    cart.add(wbox(-1.1, 0.5, -0.6, 1.1, 0.95, 0.6, cw));
    for (const sx of [-0.75, 0.75]) { const wh = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.07, 6, 16), mat('#5d4632')); wh.position.set(sx, 0.45, 0.66); cart.add(wh); const wh2 = wh.clone(); wh2.position.z = -0.66; cart.add(wh2); }
    cart.add(at(rbox(1.6, 0.18, 0.08, 0.03, cw), 1.9, 0.75, 0));
    cart.add(at(stoneBlocks(11), -0.8, 0.95, -0.2));
    root.add(at(cart, x + 6, 0, z + 5, -0.4));
    root.add(at(ladder(3.6), x + 4, 0, z - rz * 0.25 + 0.3));
    root.add(at(winch(), x - 6, 0, z - rz * 0.25 + 1.2));
    root.add(at(stoneBlocks(5), x - 12, 0, z + 2, 0.2));
    root.add(at(stoneBlocks(9), x + 10, 0, z + 1, -0.3));
    root.add(at(stoneBlocks(3), x + 1, 0, z + 7, 0.6));
    for (let i = 0; i < 3; i++) root.add(at(heap('stone', 9, 0.3, i + 7), x - 8 + i * 8, 0, z + 10));
    this.keepOut.push({ at: [x, z + 4], r: 12 });
    // boulders, big and small, round the rim
    const bm: THREE.Matrix4[] = [], bc: THREE.Color[] = [];
    for (let i = 0; i < 140; i++) {
      const a = r() * Math.PI * 2, d = 0.7 + r() * 0.5, px = x + Math.cos(a) * d * rx, pz = z + Math.sin(a) * d * rz;
      if (!inside(this.L.outline, px, pz) || this.keepOut.some((k) => Math.hypot(px - k.at[0], pz - k.at[1]) < k.r * 0.6)) continue;
      const s = 0.3 + r() ** 2 * 1.8; bm.push(M(px, s * 0.3, pz, s * (1 + r() * 0.5), s * 0.8, s, r() * 6)); bc.push(new THREE.Color(['#a29f97', '#b3afa6', '#8f8b84'][i % 3]));
    }
    root.add(inst(blobGeo(1), tintMat(1), bm, bc, { shadow: true }));
  }

  // ----- the clay pit by the stream: terracotta earth, a sandy bank, pots drying on a shelf

  g_clay(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r;
    root.add(patch(g.at, rx * 0.95, rz * 0.95, 0.005, surf({ kind: 'dirt', color: '#c9805a' }), 14, 0.2));
    root.add(patch([x - 2, z + 1], rx * 0.55, rz * 0.5, 0.008, mat('#b85a37', { rough: 1 }), 15, 0.18));
    root.add(patch([x - 2.2, z + 1.2], rx * 0.3, rz * 0.28, 0.011, mat('#9a4528', { rough: 1 }), 16, 0.15));
    // the sandy bank where the stream bends
    root.add(patch([x + rx * 0.5, z - rz * 0.35], 6, 4, 0.007, mat('#e3c48f', { rough: 1 }), 17, 0.2));
    root.add(at(shovelAndBucket(), x + 3, 0, z + 4, -0.4));
    const mounds: THREE.Matrix4[] = [], mc: THREE.Color[] = [], rr = rng(17);
    for (let i = 0; i < 26; i++) {
      const a = (i / 26) * Math.PI * 2 + rr() * 0.2, k = 0.6 + rr() * 0.1, s2 = 0.3 + rr() * 0.35;
      const px = x - 2 + Math.cos(a) * rx * k, pz = z + 1 + Math.sin(a) * rz * k;
      if (!this.clear(px, pz, 0.5)) continue;
      mounds.push(M(px, s2 * 0.15, pz, s2 * 1.4, s2 * 0.5, s2, rr() * 6)); mc.push(new THREE.Color(['#a8502f', '#b85a37', '#c46a44'][i % 3]));
    }
    root.add(inst(blobGeo(1), tintMat(1), mounds, mc));
    root.add(at(heap('clay', 9, 0.28, 3), x - 7, 0, z + 5));
    root.add(at(heap('sand', 9, 0.26, 4), x + 8, 0, z - 2));
    // a plank shelf of pots drying in the sun
    const shelf = new THREE.Group(), wood = mat('#b98a5a', { rough: 0.85 }), pot = mat('#c46a44', { rough: 0.8 });
    shelf.add(wbox(-1.6, 0.7, -0.25, 1.6, 0.78, 0.25, wood));
    for (const sx of [-1.4, 1.4]) shelf.add(at(cyl(0.05, 0.05, 0.75, wood, 6), sx, 0.37, 0));
    for (let i = 0; i < 5; i++) shelf.add(at(lathe([[0, 0], [0.16, 0], [0.22, 0.12], [0.18, 0.28], [0.12, 0.34], [0.14, 0.38], [0, 0.38]], pot, 12), -1.2 + i * 0.6, 0.78, 0));
    root.add(at(shelf, x - 6, 0, z - 4, 0.3));
    this.keepOut.push({ at: [x, z], r: Math.max(rx, rz) });
  }

  // ----- the flax fields: rows of blue-flowered flax, and stooks drying at the end

  g_flax(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r, r = rng(41);
    const stems: THREE.Matrix4[] = [], flowers: THREE.Matrix4[] = [], fc: THREE.Color[] = [];
    const blues = ['#7f97d6', '#9aaee6', '#6f88cc', '#8aa2e0'].map((c) => new THREE.Color(c));
    // four fields, each a block of rows, with grass strips between
    const fields: [number, number, number, number][] = [[x - rx, z - rz, x - 2, z - 1], [x + 2, z - rz, x + rx, z - 1], [x - rx, z + 1, x - 2, z + rz], [x + 2, z + 1, x + rx * 0.7, z + rz]];
    for (const [x0, z0, x1, z1] of fields) {
      root.add(patch([(x0 + x1) / 2, (z0 + z1) / 2], (x1 - x0) / 2, (z1 - z0) / 2, 0.005, surf({ kind: 'grass', color: '#7d8f55' }), Math.round(x0), 0.04));
      for (let zz = z0 + 0.6; zz < z1 - 0.4; zz += 0.8) for (let xx = x0 + 0.5; xx < x1 - 0.4; xx += 0.42) {
        if (!this.inGround(g, xx, zz, 1.05)) continue;
        const h = 0.8 + r() * 0.3;
        stems.push(M(xx + (r() - 0.5) * 0.2, h / 2, zz + (r() - 0.5) * 0.25, 1, h, 1, r() * 3));
        flowers.push(M(xx + (r() - 0.5) * 0.25, h + 0.02, zz + (r() - 0.5) * 0.3, 0.16, 0.08, 0.16)); fc.push(blues[Math.floor(r() * 4)]);
      }
    }
    root.add(inst(new THREE.CylinderGeometry(0.03, 0.06, 1, 3, 1, true), mat('#7a9a5a', { rough: 0.9 }), stems));
    root.add(inst(new THREE.OctahedronGeometry(1, 0), new THREE.MeshStandardMaterial({ roughness: 0.8 }), flowers, fc));
    // stooks of cut flax drying, and a scarecrow
    const straw = mat('#d8c08a', { rough: 1 });
    for (let i = 0; i < 6; i++) { const s = new THREE.Mesh(new THREE.ConeGeometry(0.45, 1.1, 7), straw); s.position.set(x + rx * 0.82 + (i % 2) * 1.2, 0.55, z + 2 + Math.floor(i / 2) * 1.6); root.add(s); }
    root.add(at(scarecrow(), x - 2, 0, z + 0.2, 0.4));
    this.keepOut.push({ at: [x, z], r: Math.max(rx, rz) * 0.9 });
  }

  // ----- the sheep meadow: a fenced pasture, the crown's sheep, a shepherd's hut and a trough

  g_pasture(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r, r = rng(51);
    root.add(patch(g.at, rx, rz, 0.004, surf({ kind: 'grass', color: '#9cbd6a' }), 31, 0.08));
    // the fence round it, with a gate gap toward the crossing
    const [cx, cz] = this.L.crossing, gateA = Math.atan2((cz - z) / rz, (cx - x) / rx), pts: XZ[] = [];
    for (let i = 0; i <= 28; i++) {
      const a = gateA + 0.25 + (i / 28) * (Math.PI * 2 - 0.5);
      pts.push([x + Math.cos(a) * rx, z + Math.sin(a) * rz]);
    }
    root.add(fence(pts));
    for (let i = 0; i < 14; i++) {
      const a = r() * Math.PI * 2, d = Math.sqrt(r()) * 0.75;
      root.add(at(sheep(i + 3), x + Math.cos(a) * d * rx, 0, z + Math.sin(a) * d * rz, r() * 6));
    }
    root.add(at(shepherdsHut(), x + rx * 0.45, 0, z - rz * 0.45, -0.5));
    root.add(at(trough(), x - rx * 0.3, 0, z + rz * 0.2, 0.3));
    // grass and clover grow in the pasture (meadowAll); trees keep out of it
    this.keepOut.push({ at: [x + rx * 0.45, z - rz * 0.45], r: 3 }, { at: [x - rx * 0.3, z + rz * 0.2], r: 2 });
    for (let i = 0; i < 24; i++) { const a = (i / 24) * Math.PI * 2; this.keepOut.push({ at: [x + Math.cos(a) * rx, z + Math.sin(a) * rz], r: 1.2 }); }
    this.noTrees.push(g);
  }

  // ----- the seed meadow and orchard: apple and plum trees in rows, wildflowers gone to seed, beehives

  g_orchard(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r, r = rng(61);
    root.add(patch(g.at, rx * 1.02, rz * 1.02, 0.004, surf({ kind: 'grass', color: '#a7c27a' }), 41, 0.12));
    for (let zz = z - rz * 0.8; zz <= z + rz * 0.8; zz += 6) for (let xx = x - rx * 0.85; xx <= x + rx * 0.85; xx += 6.5) {
      const px = xx + (Math.round(zz) % 2 ? 3 : 0), pz = zz;
      if (!this.inGround(g, px, pz, 0.92) || !this.clear(px, pz, 0.4)) continue;
      this.trees.push([px, pz, r() < 0.65 ? 'apple' : 'plum', 0.85 + r() * 0.15]);
    }
    // seed heads: dandelion clocks on stems, white
    const heads: THREE.Matrix4[] = [], stems: THREE.Matrix4[] = [];
    for (let i = 0; i < 260; i++) {
      const a = r() * Math.PI * 2, d = Math.sqrt(r()), px = x + Math.cos(a) * d * rx, pz = z + Math.sin(a) * d * rz, h = 0.35 + r() * 0.2;
      stems.push(M(px, h / 2, pz, 1, h, 1)); heads.push(M(px, h, pz, 0.09, 0.09, 0.09));
    }
    const fl: THREE.Matrix4[] = [], fc: THREE.Color[] = [];
    for (let i = 0; i < 420; i++) {
      const a = r() * Math.PI * 2, d = Math.sqrt(r()), px = x + Math.cos(a) * d * rx, pz = z + Math.sin(a) * d * rz;
      fl.push(M(px, 0.14 + r() * 0.08, pz, 0.09, 0.07, 0.09)); fc.push(new THREE.Color(FLOWERS[i % FLOWERS.length]));
    }
    this.detail.add(inst(new THREE.OctahedronGeometry(1, 0), new THREE.MeshStandardMaterial({ roughness: 0.8 }), fl, fc));
    this.detail.add(inst(new THREE.CylinderGeometry(0.012, 0.015, 1, 3), mat('#7f9f52'), stems));
    this.detail.add(inst(new THREE.OctahedronGeometry(1, 0), mat('#fbf8f0', { rough: 1 }), heads));
    for (let i = 0; i < 3; i++) root.add(at(beehive(), x + rx * 0.7, 0, z - rz * 0.3 + i * 1.6, -0.3));
    root.add(at(heap('seeds', 7, 0.18, 2), x - 2, 0, z + rz * 0.75));
    this.keepOut.push({ at: [x, z], r: Math.max(rx, rz) * 0.9 });
  }

  // ----- the pond: water where the stream ends, reeds, lily pads and a jetty

  g_pond(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r, r = rng(71);
    root.add(patch(g.at, rx * 1.12, rz * 1.15, 0.006, mat('#b9b2a4', { rough: 1 }), 51, 0.1));
    root.add(patch(g.at, rx, rz, 0.03, mat('#6fa8c6', { rough: 0.1, metal: 0.15 }), 51, 0.1));
    root.add(patch([x + 1, z - 0.5], rx * 0.6, rz * 0.55, 0.032, mat('#5f98b8', { rough: 0.1, metal: 0.15 }), 52, 0.1));
    const reeds: THREE.Matrix4[] = [], pads: THREE.Matrix4[] = [], blooms: THREE.Matrix4[] = [];
    for (let i = 0; i < 160; i++) {
      const a = r() * Math.PI * 2, d = 0.95 + r() * 0.12, h = 0.8 + r() * 0.8;
      reeds.push(M(x + Math.cos(a) * rx * d, h / 2, z + Math.sin(a) * rz * d, 1, h, 1, r()));
    }
    for (let i = 0; i < 30; i++) {
      const a = r() * Math.PI * 2, d = 0.3 + r() * 0.6, px = x + Math.cos(a) * rx * d, pz = z + Math.sin(a) * rz * d, s = 0.35 + r() * 0.25;
      pads.push(M(px, 0.045, pz, s, 0.02, s, r() * 6));
      if (i % 4 === 0) blooms.push(M(px + 0.1, 0.1, pz, 0.12, 0.08, 0.12));
    }
    this.detail.add(inst(new THREE.ConeGeometry(0.04, 1, 4), mat('#6f8f4a', { rough: 0.9 }), reeds));
    this.detail.add(inst(new THREE.CylinderGeometry(1, 1, 1, 10), mat('#5f8a48', { rough: 0.7 }), pads));
    this.detail.add(inst(new THREE.SphereGeometry(1, 8, 6), mat('#f6b8cc', { rough: 0.6 }), blooms));
    // a jetty out over the water, toward the middle
    const [cx, cz] = this.L.crossing, a = Math.atan2(cz - z, cx - x), jx = x + Math.cos(a) * rx * 0.95, jz = z + Math.sin(a) * rz * 0.95;
    const jetty = new THREE.Group(), plank = surf({ kind: 'planks', color: '#a87c4f', board: 0.2 });
    jetty.add(wbox(-0.8, 0.12, 0, 0.8, 0.24, 6, plank));
    for (const sx of [-0.75, 0.75]) for (const sz of [0.4, 3, 5.6]) jetty.add(at(cyl(0.07, 0.07, 0.9, mat('#6e4b33'), 6), sx, -0.1, sz));
    jetty.position.set(jx, 0, jz); jetty.rotation.y = Math.atan2(x - jx, z - jz);
    root.add(jetty);
    this.keepOut.push({ at: [x, z], r: Math.max(rx, rz) + 1.5 });
  }

  // ----- the scrapyard: heaps sorted by someone patient, a shed, a wheel, crates, barrels and an old bicycle

  g_scrap(g: Ground, root: THREE.Group) {
    const [x, z] = g.at, [rx, rz] = g.r;
    root.add(patch(g.at, rx, rz, 0.004, surf({ kind: 'dirt', color: '#a8957a' }), 61, 0.12));
    root.add(at(shed(), x + rx * 0.5, 0, z - rz * 0.3));
    root.add(at(scrapHeap(3, 1.8), x - rx * 0.55, 0, z - rz * 0.3));
    root.add(at(scrapHeap(7, 1.5), x - rx * 0.1, 0, z + rz * 0.5));
    root.add(at(scrapHeap(11, 1.3), x + rx * 0.6, 0, z + rz * 0.55));
    root.add(at(cartWheel(), x - 2, 0, z - 1, 0.6));
    root.add(at(crates(4), x - rx * 0.8, 0, z + rz * 0.5, 0.2));
    root.add(at(bicycle(), x + 2, 0, z + 2, 0.9));
    for (let i = 0; i < 4; i++) root.add(at(cyl(0.3, 0.3, 0.8, mat(['#4f7a8a', '#a8503a', '#5d5f63', '#c27a4a'][i], { rough: 0.6, metal: 0.3 }), 12), x + rx * 0.85 + (i % 2) * 0.7, 0.4, z - 1 + Math.floor(i / 2) * 0.7));
    this.keepOut.push({ at: [x, z], r: Math.max(rx, rz) });
  }

  // ----- benches along the paths, the picnic spot by the pond, the lookout at the far end

  private rests(root: THREE.Group) {
    for (const s of this.L.rests) {
      const [x, z] = s.at;
      if (s.kind === 'bench') root.add(at(Y.gardenBench({ color: '#7a5a3a' }), x, 0, z, s.ry ?? 0));
      else if (s.kind === 'picnic') root.add(at(picnic(), x, 0, z, 0.4));
      else if (s.kind === 'lookout') {
        root.add(at(lookout(), x, 0, z, -Math.PI / 2));
        this.pools.push([x, z, 4]);
      }
      this.keepOut.push({ at: [x, z], r: s.kind === 'lookout' ? 5 : 2.5 });
    }
  }

  // ----- everywhere else: long grass, wildflowers, a few trees and boulders, so it never reads as empty lawn

  private meadowAll(root: THREE.Group) {
    const r = rng(81), xs = this.L.outline.map((p) => p[0]), zs = this.L.outline.map((p) => p[1]);
    const [x0, x1, z0, z1] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
    const fl: THREE.Matrix4[] = [], fc: THREE.Color[] = [], tufts: THREE.Matrix4[] = [], tc: THREE.Color[] = [], rocks: THREE.Matrix4[] = [];
    const inAny = (x: number, z: number) => this.grounds.some((g) => g.kind !== 'pasture' && this.inGround(g, x, z, g.kind === 'wood' ? 1.08 : 1.02));
    for (let i = 0; i < 9000; i++) {
      const x = x0 + r() * (x1 - x0), z = z0 + r() * (z1 - z0);
      if (!inside(this.L.outline, x, z) || inAny(x, z) || !this.clear(x, z)) continue;
      const k = r();
      if (k < 0.55) { fl.push(M(x, 0.1 + r() * 0.1, z, 0.07, 0.06, 0.07)); fc.push(new THREE.Color(FLOWERS[i % FLOWERS.length])); }
      else if (k < 0.85) { const s = 0.9 + r() * 0.7; tufts.push(M(x, 0, z, s, s, s, r() * 6)); tc.push(new THREE.Color(['#7f9f52', '#8aab5c', '#6f8f4a', '#97b465'][i % 4])); }
      else if (k < 0.99) continue;
      else rocks.push(M(x, 0.1, z, 0.4 + r() * 0.5, 0.3, 0.35 + r() * 0.4, r() * 6));
    }
    this.detail.add(inst(new THREE.OctahedronGeometry(1, 0), new THREE.MeshStandardMaterial({ roughness: 0.8 }), fl, fc));
    this.detail.add(inst(tuftGeo(), new THREE.MeshStandardMaterial({ roughness: 0.9, flatShading: true }), tufts, tc));
    this.detail.add(inst(blobGeo(0), new THREE.MeshStandardMaterial({ color: '#a29f97', roughness: 1, flatShading: true }), rocks));
    // copses and lone trees between the grounds
    let n = 0;
    for (let i = 0; i < 2000 && n < 150; i++) {
      const x = x0 + r() * (x1 - x0), z = z0 + r() * (z1 - z0);
      if (!inside(this.L.outline, x, z) || inAny(x, z) || !this.clear(x, z, 1.5) || this.noTrees.some((g) => this.inGround(g, x, z, 1.1))) continue;
      if (this.trees.some(([tx, tz]) => Math.hypot(tx - x, tz - z) < 4)) continue;
      // the trees gather toward the shore, leaving the middle open
      let edge = Infinity; for (let j = 0; j < this.L.outline.length; j += 3) edge = Math.min(edge, Math.hypot(this.L.outline[j][0] - x, this.L.outline[j][1] - z));
      if (edge > 22 && r() < 0.8) continue;
      this.trees.push([x, z, ['oak', 'lime', 'birch', 'cherry', 'pine'][n % 5], 0.95 + r() * 0.3]); n++;
    }
  }
}

// ---------------------------------------------------------------------------
// little things

// a tuft of grass: five blades splayed from one root
let tuft: THREE.BufferGeometry | null = null;
function tuftGeo() {
  if (tuft) return tuft;
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 5; i++) {
    const c = new THREE.ConeGeometry(0.05, 0.55 + (i % 3) * 0.12, 3); c.translate(0, 0.28, 0);
    c.rotateZ((i - 2) * 0.22); c.rotateY(i * 1.3); parts.push(c);
  }
  tuft = mergeGeometries(parts); tuft.computeVertexNormals();
  return tuft;
}

function scarecrow() {
  const g = new THREE.Group(), wood = mat('#8a6a4a', { rough: 0.9 });
  g.add(at(cyl(0.05, 0.05, 2.0, wood, 6), 0, 1.0, 0));
  g.add(at(rbox(1.4, 0.07, 0.07, 0.02, wood), 0, 1.45, 0));
  g.add(at(rbox(0.5, 0.6, 0.25, 0.05, mat('#c8547e', { rough: 0.9 })), 0, 1.3, 0));
  const head = sphere(0.2, mat('#e3c48f', { rough: 1 }), 10, 8); head.position.y = 1.85; g.add(head);
  const hat = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.3, 12), mat('#c9a06a', { rough: 1 })); hat.position.y = 2.1; g.add(hat);
  return g;
}

function shepherdsHut() {
  const g = new THREE.Group();
  g.add(wbox(-1.4, 0.5, -0.9, 1.4, 2.0, 0.9, mat('#5f8a7a', { rough: 0.8 })));
  const roof = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.05, 3.0, 16, 1, false, -Math.PI / 2, Math.PI), mat('#a7aeb3', { rough: 0.5, metal: 0.3 }));
  roof.rotation.z = Math.PI / 2; roof.scale.set(1, 1, 1); roof.position.y = 2.0; g.add(roof);
  for (const x of [-1.0, 1.0]) for (const s of [-1, 1]) { const w = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.06, 6, 14), mat('#3a3330')); w.position.set(x, 0.34, s * 0.95); g.add(w); }
  g.add(wbox(-0.35, 0.5, 0.9, 0.35, 1.7, 0.95, mat('#f2c14e', { rough: 0.7 })));
  g.add(wbox(-0.5, 0.0, 0.95, 0.5, 0.5, 1.5, mat('#8a6a4a')));
  return g;
}

function trough() {
  const g = new THREE.Group(), wood = mat('#8a6a4a', { rough: 0.9 });
  g.add(wbox(-1.0, 0.2, -0.3, 1.0, 0.6, 0.3, wood));
  g.add(wbox(-0.92, 0.6, -0.22, 0.92, 0.62, 0.22, mat('#7fb6cf', { rough: 0.1 })));
  for (const sx of [-0.9, 0.9]) g.add(wbox(sx - 0.08, 0, -0.25, sx + 0.08, 0.2, 0.25, wood));
  return g;
}

function beehive() {
  const g = new THREE.Group(), w = mat('#f3e3c8', { rough: 0.8 });
  g.add(wbox(-0.3, 0, -0.3, 0.3, 0.3, 0.3, mat('#8a6a4a')));
  for (let i = 0; i < 3; i++) g.add(wbox(-0.32, 0.3 + i * 0.26, -0.32, 0.32, 0.54 + i * 0.26, 0.32, w));
  const roof = new THREE.Mesh(new THREE.ConeGeometry(0.5, 0.3, 4), mat('#c8547e', { rough: 0.8 })); roof.rotation.y = Math.PI / 4; roof.position.y = 1.2; g.add(roof);
  return g;
}

function picnic() {
  const g = new THREE.Group();
  const c = document.createElement('canvas'); c.width = c.height = 64;
  const x = c.getContext('2d')!;
  for (let i = 0; i < 8; i++) for (let j = 0; j < 8; j++) { x.fillStyle = (i + j) % 2 ? '#f6f0e6' : '#d9473f'; x.fillRect(i * 8, j * 8, 8, 8); }
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  const blanket = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 2.0), new THREE.MeshStandardMaterial({ map: tex, roughness: 1 }));
  blanket.rotation.x = -Math.PI / 2; blanket.position.y = 0.02; g.add(blanket);
  const basket = new THREE.Group(), wick = mat('#b98a5a', { rough: 0.9 });
  basket.add(at(rbox(0.6, 0.35, 0.4, 0.05, wick), 0, 0.17, 0));
  const handle = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.025, 6, 16, Math.PI), wick); handle.position.y = 0.35; basket.add(handle);
  basket.position.set(0.6, 0, -0.3); g.add(basket);
  for (let i = 0; i < 3; i++) { const a = sphere(0.09, mat(['#d9473f', '#e8a33c', '#7aa64e'][i]), 8, 6); a.position.set(-0.4 + i * 0.22, 0.09, 0.4); g.add(a); }
  g.add(at(cyl(0.12, 0.1, 0.2, mat('#f6f0e6'), 12), -0.5, 0.1, -0.4));
  return g;
}

function lookout() {
  const g = new THREE.Group(), wood = mat('#8a6a4a', { rough: 0.85 }), dark = mat('#6e4b33', { rough: 0.85 });
  const H = 5.2;
  for (const [x, z] of [[-1.2, -1.2], [1.2, -1.2], [1.2, 1.2], [-1.2, 1.2]]) g.add(at(cyl(0.12, 0.14, H + 1.2, wood, 8), x, (H + 1.2) / 2, z));
  for (const y of [1.6, 3.4]) for (const [a, b] of [[[-1.2, -1.2], [1.2, 1.2]], [[1.2, -1.2], [-1.2, 1.2]]] as number[][][]) {
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]), bar = rbox(len, 0.1, 0.1, 0.02, dark);
    bar.position.set(0, y, 0); bar.rotation.y = Math.atan2(-(b[1] - a[1]), b[0] - a[0]); g.add(bar);
  }
  g.add(wbox(-1.5, H, -1.5, 1.5, H + 0.18, 1.5, surf({ kind: 'planks', color: '#a87c4f', board: 0.2 })));
  for (const s of [-1, 1]) { g.add(wbox(-1.5, H + 0.9, s * 1.5 - 0.05, 1.5, H + 1.0, s * 1.5 + 0.05, wood)); g.add(wbox(s * 1.5 - 0.05, H + 0.9, -1.5, s * 1.5 + 0.05, H + 1.0, 1.5, wood)); }
  const roof = new THREE.Mesh(new THREE.ConeGeometry(2.3, 1.4, 4), mat('#b5523b', { rough: 0.8 })); roof.rotation.y = Math.PI / 4; roof.position.y = H + 2.0; g.add(roof);
  const lad = ladder(H + 0.3); lad.position.set(0, 0, 1.9); g.add(lad);
  const lamp = sphere(0.18, mat('#ffd9a0', { emissive: '#ffb86b', emissiveIntensity: 2 }), 10, 8); lamp.position.set(0, H + 1.15, 0); lamp.userData.noShadow = true; g.add(lamp);
  const sb = signboard('THE LOOKOUT', 'the whole Commons from here', '#b5523b'); sb.position.set(2.6, 0, 1.5); sb.scale.setScalar(0.7); g.add(sb);
  return g;
}
